import { action, observable, makeObservable } from "mobx";
import ShipmentsAPI from "../api/ShipmentsAPI";
import {
  BulkUpdate,
  DeliveryPartner,
  ShipmentMethod,
  Shipments,
  ShipmentsDetails,
  TabWiseShipmentsCount,
  Swagpack,
} from "../models/Shipments";
import { FilterValue } from "../models/Filters/FilterValue";
import { ShipmentStatusEnum } from "../models/Shipments/ShipmentStatus";
import { ShipmentDetailsApiRow } from "../models/Shipments/ShipmentDetails";
import { ShipmentRequest } from "../models/AddShipment";
import { UserStore } from "./UserStore";

const ShipmentStatus = [
  { id: 1, value: "New" },
  { id: 2, value: "Ready To Ship" },
  { id: 3, value: "Shipped" },
  { id: 4, value: "Out for delivery" },
  { id: 5, value: "Delivered" },
  { id: 6, value: "Return Requested" },
  { id: 7, value: "Returned" },
  { id: 8, value: "Cancelled" },
];
export interface GetShipmentsParams {
  page?: number;
  limit?: number;
  tab_id?: string;
  sortBy?: string;
  allSelected?: boolean;
  sortType?: "ASC" | "DESC";
  search?: string;
  shipment_type?: number[];
  status?: number[];
  delivery_partner_id?: number[];
  order_id?: number[];
  dimensions?: number[];
  emails?: string;
  c_id?: FilterValue[];
  inventory_type?: string;
  pack_ids?: FilterValue[];
  packs_exist?: boolean | undefined;
  country?: string[] | undefined;
  search_text_value?: (number | string)[] | undefined;
  contact_number?: string | undefined;
}

export interface GetPacksParams {
  shipment_ids: number[];
  available_only?: boolean;
  consumed_only?: boolean;
  search_text?: string;
}

export interface GetPacksResponse {
  result: Swagpack[];
  total_count: number;
  total_pages: number;
}

export class ShipmentStore {
  shipmentsAPI: ShipmentsAPI;
  shipments?: Shipments[] = [];
  totalCount: number = 0;
  totalPages: number = 0;
  deliveryPartners: DeliveryPartner[] = [];
  loading: boolean = false;
  error: string | null = null;
  currentPacks: Swagpack[] = [];
  orderIds: any[];
  c_id: any[] = [];
  searchedPacks: FilterValue[];
  countByStatus: TabWiseShipmentsCount[] = [];

  constructor() {
    this.shipmentsAPI = new ShipmentsAPI();
    this.orderIds = [];
    this.searchedPacks = [];

    makeObservable(this, {
      shipments: observable,
      totalCount: observable,
      totalPages: observable,
      getShipments: action,
      editShipment: action,
      deleteShipment: action,
      deliveryPartners: observable,
      currentPacks: observable,
      loading: observable,
      error: observable,
      reset: action,
      getDeliveryPartner: action,
      orderIds: observable,
      getOrder: action,
      searchedPacks: observable,
      searchPacksByName: action,
      createShipmentThroughCsv: action,
      createBulkShipmentsViaInventoryIdAndCsv: action,
      countByStatus: observable,
      getCountByStatus: action,
      moveShipmentToNew: action,
    });
  }

  reset = () => {
    this.shipments = [];
    this.totalCount = 0;
    this.totalPages = 0;
    this.deliveryPartners = [];
    this.loading = false;
    this.error = null;
    this.currentPacks = [];
    this.countByStatus = [];
    this.orderIds = [];
    this.searchedPacks = [];
  };

  setLoadingAndError = action(
    (loading: boolean, errorMessage: string | null) => {
      this.loading = loading;
      this.error = errorMessage;
    },
  );

  getShipments = async (
    params: GetShipmentsParams = {},
    download_csv: boolean = false,
    signal?: AbortSignal,
  ) => {
    try {
      if (!download_csv) {
        this.setLoadingAndError(true, null);
      }

      const response = await this.shipmentsAPI.getShipments(
        params,
        download_csv,
        signal,
      );
      if (!download_csv) {
        this.shipments = response.list;
        this.totalCount = response.total_count;
        this.totalPages = response.total_pages;
      }
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      // Ignore abort - request was cancelled on tab/filter change
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
        this.setLoadingAndError(false, null);
        return;
      }
      this.setLoadingAndError(false, error?.response?.data?.message);
    }
  };

  downloadBulkShipmentsCSVReport = async (params: GetShipmentsParams = {}) => {
    try {
      const response =
        await this.shipmentsAPI.downloadBulkShipmentsCSVReport(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  getDeliveryPartner = async (
    searchText?: string,
    isLogisticVendor?: boolean,
    isPrinter?: boolean,
  ) => {
    try {
      const response = await this.shipmentsAPI.getDeliveryPartner(
        searchText,
        isLogisticVendor,
        isPrinter,
      );
      if (response.success) {
        this.deliveryPartners = response.result.list;
      }
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    }
  };

  editShipment = async (
    shipmentId: number,
    shipment: ShipmentsDetails,
    userStore?: UserStore,
  ) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.editShipment(
        shipmentId,
        shipment,
      );

      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          const statusValue = shipment.status
            ? ShipmentStatus.find((s) => s.id === shipment.status)?.value
            : undefined;

          const deliveryPartnerValue = shipment.delivery_partner_id
            ? this.deliveryPartners.find(
                (dp) => dp.delivery_partner_id === shipment.delivery_partner_id,
              )?.delivery_partner_name
            : undefined;

          const modifiedDate = new Date().toISOString();
          const modifiedById = userStore?.user?.id;
          const modifiedByName = userStore?.user?.name;

          this.shipments[index] = {
            ...this.shipments[index],
            ...shipment,
            ...(shipment.status !== undefined && {
              status: shipment.status,
              status_label: statusValue,
            }),
            ...(shipment.delivery_partner_id !== undefined && {
              delivery_partner_id: shipment.delivery_partner_id,
              delivery_partner_name: deliveryPartnerValue,
            }),
            modified_date: modifiedDate,
            ...(modifiedById !== undefined && { modified_by_id: modifiedById }),
            ...(modifiedByName !== undefined && {
              modified_by_name: modifiedByName,
            }),
          };
        }
      }

      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  deleteShipment = async (
    shipmentId: number,
    status: ShipmentStatusEnum,
    reason?: string,
  ) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.deleteShipment(
        shipmentId,
        status,
        reason,
      );
      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          this.shipments[index] = {
            ...this.shipments[index],
            status: 8,
            status_label: "Cancelled",
          };
        }
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  removePacksFromShipment = async (shipmentId: number) => {
    this.setLoadingAndError(true, null);
    try {
      const response =
        await this.shipmentsAPI.removePacksFromShipment(shipmentId);
      if (response.success && this.shipments) {
        const index = this.shipments.findIndex(
          (item) => item.id === shipmentId,
        );
        if (index !== -1) {
          this.shipments[index] = {
            ...this.shipments[index],
            packs_label: "--",
          };
        }
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  moveShipmentToNew = async (shipmentId: number) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.moveShipmentToNew(shipmentId);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      return undefined;
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  bulkShipmentUpdate = async (
    params: GetShipmentsParams,
    shipment_ids: number[],
    length?: number,
    breadth?: number,
    height?: number,
    weight?: number,
    status?: number,
    delivery_partner_id?: number,
    shipment_method?: ShipmentMethod,
  ) => {
    this.setLoadingAndError(true, null);
    try {
      const payload: BulkUpdate = {
        shipment_ids,
        ...(length !== undefined && { length }),
        ...(breadth !== undefined && { breadth }),
        ...(height !== undefined && { height }),
        ...(weight !== undefined && { weight }),
        ...(status !== undefined && { status }),
        ...(delivery_partner_id !== undefined && { delivery_partner_id }),
        ...(shipment_method !== undefined && { shipment_method }),
      };

      const response = await this.shipmentsAPI.bulkShipmentUpdate(
        payload,
        params,
      );
      if (response.success && this.shipments) {
        shipment_ids.forEach((id) => {
          const index = this.shipments!.findIndex((item) => item.id === id);
          if (index !== -1 && this.shipments) {
            const statusValue =
              status !== undefined
                ? ShipmentStatus.find((s) => s.id === status)?.value
                : undefined;

            const deliveryPartnerName =
              delivery_partner_id !== undefined
                ? this.deliveryPartners.find(
                    (dp: any) => dp.delivery_partner_id === delivery_partner_id,
                  )?.delivery_partner_name
                : undefined;

            this.shipments[index] = {
              ...this.shipments[index],
              ...(length !== undefined && { length }),
              ...(breadth !== undefined && { breadth }),
              ...(height !== undefined && { height }),
              ...(weight !== undefined && { weight }),
              ...(status !== undefined && {
                status,
                status_label: statusValue,
              }),
              ...(delivery_partner_id !== undefined && {
                delivery_partner_id,
                delivery_partner_name: deliveryPartnerName,
              }),
              ...(shipment_method !== undefined && { shipment_method }),
            };
          }
        });
      }

      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  uploadCsvForBulkEdit = async (file: File | null) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.uploadCsvForBulkEdit(file);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  processShipments = async (shipmentIds: number[]) => {
    try {
      const response = await this.shipmentsAPI.processShipments(shipmentIds);

      if (response.success && this.shipments) {
        shipmentIds.forEach((shipmentId) => {
          const index = this.shipments!.findIndex(
            (item) => item.id === shipmentId,
          );
          if (index !== -1 && this.shipments) {
            this.shipments[index] = {
              ...this.shipments[index],
              dispatch_status: 1,
            };
          }
        });
      }

      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  getOrder = async (orderId: any) => {
    try {
      const response = await this.shipmentsAPI.getOrder(orderId);
      if (response) {
        this.orderIds = (response?.result || []).map((order: any) => ({
          id: String(order.order_id),
          value: String(order.order_id),
        }));
      }
      return response.result;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    } finally {
      this.setLoadingAndError(false, this.error);
    }
  };

  downloadShiprocketCSV = async (params: GetShipmentsParams = {}) => {
    try {
      const response = await this.shipmentsAPI.downloadShiprocketCSV(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  getShiprocketCSVBulkReport = async (params: GetShipmentsParams = {}) => {
    try {
      const response =
        await this.shipmentsAPI.getShiprocketCSVBulkReport(params);
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
      throw error;
    }
  };

  getPacksByInventoryId = async (inventory_id: number, search?: string) => {
    try {
      this.currentPacks = [];
      const response = await this.shipmentsAPI.getPacksByInventoryId(
        inventory_id,
        search,
      );
      this.currentPacks = response.result;
      return response;
    } catch (error: any) {
      console.log("error", error);
    }
  };

  getShipmentPacks = async (params: GetPacksParams) => {
    try {
      const response = await this.shipmentsAPI.getShipmentPacks(params);
      this.currentPacks = response.result;
      return response;
    } catch (error: any) {
      this.currentPacks = [];
      console.log("error", error);
    }
  };

  savePacks = async (shipmentIds: number[], shipmentPacks: Swagpack) => {
    this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.savePacks(
        shipmentIds,
        shipmentPacks,
      );
      this.setLoadingAndError(false, null);
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    }
  };

  searchPacksByName = async (searchText: string) => {
    try {
      const response = await this.shipmentsAPI.searchPacks(searchText);

      if (response?.result) {
        this.searchedPacks = response.result;
      }
      return response;
    } catch (error: any) {
      this.setLoadingAndError(false, error?.response?.data?.message);
    }
  };

  createShipment = async (shipments: ShipmentRequest) => {
    // this.setLoadingAndError(true, null);
    try {
      const response = await this.shipmentsAPI.createShipment(shipments);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  createShipmentThroughCsv = async (orderId: number, file: File | null) => {
    try {
      const response = await this.shipmentsAPI.createShipmentThroughCsv(
        orderId,
        file,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  createBulkShipmentsViaInventoryIdAndCsv = async (
    inventoryId: number,
    file: File | null,
  ) => {
    try {
      const response =
        await this.shipmentsAPI.createBulkShipmentsViaInventoryIdAndCsv(
          inventoryId,
          file,
        );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getCountByStatus = async () => {
    try {
      const response = await this.shipmentsAPI.getCountByStatus();
      if (response.success) {
        this.countByStatus = response.result;
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getShipmentDetails = async (
    shipmentId: number,
  ): Promise<ShipmentDetailsApiRow> => {
    const response = await this.shipmentsAPI.getShipmentDetails(shipmentId);
    if (!response?.success) {
      throw new Error(response?.message || "Failed to fetch shipment details");
    }
    return response.result;
  };
}
